import React, { useState } from 'react';
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Add } from "@mui/icons-material";
import { DaButton } from "../form/form.styles";
import { MessageBox } from "./MessageBox";
import { IMessagePanelProps, MsgBoxType, IUserAutocompleteDialogProps } from "./types";
import { throttle } from "lodash";
import { ILookupBroker, ILookupUserSearchTerm, lookupApi } from "../../../_api";
import { useAuth } from "../../../_auth/useAuth";

export const BrokerAddDialog: React.FC<IUserAutocompleteDialogProps> = (
    {
        isOpen = false,
        onClose,
        title = "Choose Authorized Broker",
        userId = -1,
        onCreateBroker,
        isBrokerRequested = false,
    }: IUserAutocompleteDialogProps
) => {
    const { getAccessToken } = useAuth();
    const [selItem, setSelItem] = React.useState<number | undefined>(undefined);
    const [brokerOptions, setBrokerOptions] = useState<Array<ILookupBroker>>([]);
    const [open, setOpen] = useState(false);
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });
    const [searchTerm, setSearchTerm] = useState<string>('');

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    const handleClose = () => {
        onClose("cancel");
        setSelItem(undefined);
        setSearchTerm('');
    };

    const handleOk = () => {
        onClose("ok", selItem);
        setSelItem(undefined);
        setSearchTerm('');
    };

    const handleCreateBroker = () => {
        onClose("create-broker");
        setSearchTerm('');
        if (onCreateBroker) {
            onCreateBroker();
        }
    };

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const lookupSearch = React.useRef(
        throttle(async (search_term) => {
            if (search_term.length < 3) {
                setBrokerOptions([]);
            } else {
                setBrokerOptions(await getLookupOptions(search_term) as Array<ILookupBroker>);
            }
        }, 500)
    ).current;

    const lookupInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {
        setSearchTerm(newValue);
        // Don't re-run search for programmatic change or user selection
        if (reason !== "reset") {
            setOpen(true);
            lookupSearch(newValue);
        }
    }

    const getLookupOptions = async (search_term: string): Promise<Array<ILookupBroker> | void> => {
        let options: ILookupUserSearchTerm = { user_id: userId, search_term: search_term };
        const auth_token = await getAccessToken();
        let ret_val = [];
        try {

            if (isBrokerRequested) {
                options.company_type = "LISTADMIN";
                const resp = await lookupApi.get_user_customer_lookup(options, auth_token);
                if (resp) {
                    if (resp.success) {
                        ret_val = resp.data;
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while retrieving Authorized Customer lookups data. " +
                            "The Authorized Customer lookups data was not found.",
                            "Authorized Customer lookups Not Found Error",
                            "error"
                        );
                    }
                }
            } else {
                const resp = await lookupApi.get_user_listadmin_lookup(options, auth_token);
                if (resp) {
                    if (resp.success) {
                        ret_val = resp.data;
                    }
                    else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while retrieving Authorized Broker lookups data. " +
                            "The Authorized Broker lookups data was not found.",
                            "Authorized Broker lookups Not Found Error",
                            "error"
                        );
                    }
                }
            }
            return ret_val;
        }
        catch (err) {
            msgBox_Show(
                "An error occurred while fetching Authorized Broker lookups data. " +
                "There may have been a network error.",
                "Lookup API Fetch Error",
                "error"
            );
        }
        return ret_val;
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                fullWidth={true}
                maxWidth={"sm"}
                aria-labelledby="dialog-box-title"
                aria-describedby="dialog-box-description"
            >
                <DialogTitle
                    id="dialog-box-title"
                    sx={{
                        display: "flex",
                        alignItems: "end",
                        justifyContent: "start"
                    }}
                >
                    {title}
                </DialogTitle>
                <DialogContent sx={{ whiteSpace: "pre-line" }}>
                    <Autocomplete
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        inputValue={searchTerm}
                        filterOptions={(x) => x}
                        size={formFieldSize}
                        fullWidth
                        id=""
                        options={brokerOptions.map(({ company_id, company_sid, company_name, listadmin_sid, listadmin_id, email, company_domain }) =>
                            ({ key: listadmin_id || company_id, value: company_id || listadmin_id, label: `${company_sid || listadmin_sid} | ${company_name} ${email ? "| " + email : ""} ${company_domain ? "| @" + company_domain : ""}` })
                        )}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => {
                            let newValue = value?.value ?? undefined;
                            setSelItem(newValue);
                        }}
                        onInputChange={lookupInputChanged}
                        isOptionEqualToValue={(option, value) =>
                            value === undefined || option.value === value.value
                        }
                        noOptionsText={
                            searchTerm.length >= 3 ? (
                                <Box>
                                    <Box>No brokers found</Box>
                                    {isBrokerRequested && <DaButton
                                        buttonStyle="primary"
                                        startIcon={<Add />}
                                        onClick={handleCreateBroker}
                                        sx={{ mt: 1 }}
                                    >
                                        Create Broker
                                    </DaButton>}
                                </Box>
                            ) : "Type at least 3 characters to search"
                        }
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.key}>
                                {option.label}
                            </Box>
                        )}
                        renderInput={(params) =>
                            <TextField {...params}
                                label="Authorized Broker"
                                variant={formFieldVariant}
                            />
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <DaButton buttonStyle="secondary" variant={"outlined"} autoFocus onClick={handleClose}>Cancel</DaButton>
                    <DaButton buttonStyle="primary" variant={"outlined"} onClick={handleOk}>OK</DaButton>
                </DialogActions>
            </Dialog>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
        </>
    );
}